import { useEffect, useState } from 'react'
import { nodes as api } from '@/api/endpoints'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Err } from '@/components/Err'
import { SHELL_PING, shellInput, shellOutput, shellResize } from '@/domain/nodeShell'

// 노드 상세 제목 줄의 고급 칩 `콘솔`(#480) — 부팅이 끝난 노드의 root 셸. 열면 단회 경로를 받아 같은 origin 의 WebSocket 을 열고,
// 로그인은 백엔드가 이미 끝냈으므로 여기서는 셸의 입출력만 나른다. xterm 은 열 때만 불러온다.

type Status = 'connecting' | 'connected' | 'disconnected'

/** 제목 줄의 고급 칩과 그 대화 상자. */
export function NodeShellChip({ nodeId, name, className }: { nodeId: string; name: string; className: string }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <button type="button" className={className} onClick={() => setOpen(true)}>
        콘솔
      </button>
      {open ? <NodeShellDialog nodeId={nodeId} name={name} onClose={() => setOpen(false)} /> : null}
    </>
  )
}

export function NodeShellDialog({ nodeId, name, onClose }: { nodeId: string; name: string; onClose: () => void }) {
  // 터미널 자리는 state 다: Radix 가 대화 상자를 한 박자 뒤에 붙이므로 요소가 생긴 뒤에 연결을 시작한다.
  const [screen, setScreen] = useState<HTMLDivElement | null>(null)
  const [status, setStatus] = useState<Status>('connecting')
  const [error, setError] = useState<unknown>(null)

  useEffect(() => {
    const el = screen
    if (!el) return
    let cancelled = false
    let socket: WebSocket | null = null
    let ping: ReturnType<typeof setInterval> | null = null
    let dispose: (() => void) | null = null
    const run = async () => {
      try {
        const opened = await api.shell(nodeId)
        const [{ Terminal }, { FitAddon }] = await Promise.all([import('@xterm/xterm'), import('@xterm/addon-fit'), import('@xterm/xterm/css/xterm.css')])
        if (cancelled) return
        const term = new Terminal({ cursorBlink: true, fontSize: 13, convertEol: false })
        const fit = new FitAddon()
        term.loadAddon(fit)
        term.open(el)
        fit.fit()
        const ws = new WebSocket(`${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}${opened.url}`, ['binary'])
        ws.binaryType = 'arraybuffer'
        socket = ws
        let connected = false
        ws.onopen = () => ws.send(shellResize(term.cols, term.rows))
        ws.onmessage = (ev) => {
          const out = shellOutput(connected, new Uint8Array(ev.data as ArrayBuffer))
          if (out.connected && !connected) {
            connected = true
            setStatus('connected')
            term.focus()
          }
          if (out.data.length > 0) term.write(out.data)
        }
        ws.onclose = () => setStatus('disconnected')
        const input = term.onData((d) => ws.readyState === WebSocket.OPEN && ws.send(shellInput(d)))
        const resize = term.onResize(({ cols, rows }) => ws.readyState === WebSocket.OPEN && ws.send(shellResize(cols, rows)))
        const refit = () => fit.fit()
        window.addEventListener('resize', refit)
        ping = setInterval(() => ws.readyState === WebSocket.OPEN && ws.send(SHELL_PING), 30_000)
        dispose = () => {
          window.removeEventListener('resize', refit)
          input.dispose()
          resize.dispose()
          term.dispose()
        }
      } catch (err) {
        if (!cancelled) setError(err)
      }
    }
    void run()
    return () => {
      cancelled = true
      if (ping) clearInterval(ping)
      socket?.close()
      dispose?.()
    }
  }, [nodeId, screen])

  return (
    <Dialog open onOpenChange={(v) => (v ? undefined : onClose())}>
      <DialogContent
        aria-describedby={undefined}
        className="flex h-[88vh] w-[92vw] max-w-none flex-col gap-2 p-3 sm:max-w-none"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="pr-8">
          <DialogTitle className="flex items-baseline gap-2">
            <span>{name} 콘솔</span>
            <span className="text-[11px] font-normal text-muted-foreground">
              {status === 'connecting' ? '연결 중' : status === 'disconnected' ? '연결이 끊겼습니다.' : ''}
            </span>
          </DialogTitle>
        </DialogHeader>
        <Err error={error} />
        <div ref={setScreen} className="min-h-0 flex-1 overflow-hidden rounded-md bg-black p-1" />
      </DialogContent>
    </Dialog>
  )
}
